/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@vaas/react-ui-kit/auth';

import { Icon, type IconName } from '@/components/Icon';
import i18n from '@/i18n/i18n';
import { useUiStore } from '@/stores/uiStore';
import { submitFeedbackFlow, type UserFeedbackRequest } from '@/api/feedback';
import { Alert, Button } from '@/design-system';
import { cn } from '@/lib/cn';
import { ShellDialog } from './ShellDialog';

const TYPES: [string, string, IconName][] = [
  ['bug', 'feedback.types.bug', 'warning'],
  ['suggestion', 'feedback.types.suggestion', 'auto_awesome'],
  ['question', 'feedback.types.question', 'help'],
  ['data', 'feedback.types.data', 'list'],
  ['performance', 'feedback.types.performance', 'analytics'],
  ['ui', 'feedback.types.ui', 'schema'],
];

const MIN_CHARS = 20;

/** Screenshot of current page (excludes feedback modal). */
async function captureScreenshot(): Promise<File | null> {
  try {
    const html2canvas = (await import('html2canvas')).default;
    const canvas = await html2canvas(document.body, {
      logging: false,
      useCORS: true,
      // Leave the feedback dialog (and any other overlay) out of the picture of the page.
      ignoreElements: (el) =>
        Boolean(el.classList?.contains('scrim') || el.closest?.('[role="dialog"]')),
    });
    const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    return blob ? new File([blob], 'screenshot.png', { type: 'image/png' }) : null;
  } catch {
    return null;
  }
}

/** Draggable feedback FAB + dialog (auto-attaches context + screenshot). */
export function FeedbackFab() {
  const { t } = useTranslation();
  const open = useUiStore((s) => s.feedbackOpen);
  const openFeedback = useUiStore((s) => s.openFeedback);
  const closeFeedback = useUiStore((s) => s.closeFeedback);
  const { account } = useAuth();

  const [type, setType] = useState('bug');
  const [rating, setRating] = useState(0);
  const [text, setText] = useState('');
  const [sent, setSent] = useState(false);
  const [shot, setShot] = useState(true);
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const moved = useRef(false);
  const { pathname } = useLocation();

  const trimmed = text.replace(/\s/g, '').length;
  const canSend = trimmed >= MIN_CHARS && !sending;
  const remaining = MIN_CHARS - trimmed;
  const screen =
    pathname === '/' ? t('feedback.screenHome') : pathname.replace(/^\//, '').replace(/-/g, ' ');

  const reset = () => {
    setType('bug');
    setRating(0);
    setText('');
    setSent(false);
    setShot(true);
    setFiles([]);
    setError(null);
    setSending(false);
  };
  const close = () => {
    closeFeedback();
    reset();
  };

  const send = async () => {
    setSending(true);
    setError(null);
    const attachments: File[] = [];
    if (shot) {
      const screenshot = await captureScreenshot();
      if (screenshot) attachments.push(screenshot);
    }
    attachments.push(...files);

    /* v8 ignore next -- defensive: type always equals one of the TYPES option values. */
    const typeKey = TYPES.find(([v]) => v === type)?.[1] ?? type;
    const typeLabel = i18n.t(typeKey);
    const [firstName, ...rest] = (account?.name ?? '').trim().split(' ');
    const request: UserFeedbackRequest = {
      feedbackForm: {
        firstName: firstName || undefined,
        lastName: rest.join(' ') || undefined,
        email: account?.username ?? undefined,
        urlViewSelection: window.location.href,
        issueDescription: `[${typeLabel}${rating ? ` · ${rating}/5` : ''}] ${text}`,
      },
      url: window.location.href,
      userId: account?.gmin ?? '',
      userName: account?.name ?? '',
      type: 'INTERNAL',
    };
    try {
      await submitFeedbackFlow(request, attachments);
      setSent(true);
    } catch {
      setError(t('feedback.error'));
    } finally {
      setSending(false);
    }
  };

  const startDrag = (e: React.PointerEvent<HTMLButtonElement>) => {
    const btn = e.currentTarget;
    const rect = btn.getBoundingClientRect();
    const offX = e.clientX - rect.left;
    const offY = e.clientY - rect.top;
    const startX = e.clientX;
    const startY = e.clientY;
    moved.current = false;
    const onMove = (ev: PointerEvent) => {
      // Drag threshold: distinguish click from incidental pointer jitter.
      if (!moved.current && Math.hypot(ev.clientX - startX, ev.clientY - startY) < 4) return;
      moved.current = true;
      // Clamp within the viewport (with a small margin) so the button can never
      // be dragged off-screen where it becomes unreachable without zooming out.
      const margin = 8;
      const maxLeft = window.innerWidth - rect.width - margin;
      const maxTop = window.innerHeight - rect.height - margin;
      const left = Math.min(Math.max(ev.clientX - offX, margin), Math.max(margin, maxLeft));
      const top = Math.min(Math.max(ev.clientY - offY, margin), Math.max(margin, maxTop));
      btn.style.left = `${left}px`;
      btn.style.top = `${top}px`;
      btn.style.right = 'auto';
      btn.style.bottom = 'auto';
    };
    // Tear down on pointerup AND pointercancel: if the pointer is released
    // outside the window or the gesture is canceled, pointerup may never fire,
    // which would otherwise leave the move listener attached and the button
    // "stuck" to the cursor.
    const endDrag = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
  };

  return (
    <>
      <button
        type="button"
        className="fb-fab"
        data-tour="feedback"
        onPointerDown={startDrag}
        onClick={() => {
          if (moved.current) {
            moved.current = false;
            return;
          }
          openFeedback();
        }}
        title={t('feedback.title')}
        aria-label={t('feedback.title')}
      >
        <Icon name="feedback" size={18} />
      </button>
      {open && (
        <ShellDialog
          onClose={close}
          icon="feedback"
          title={t('feedback.title')}
          description={t('feedback.modalSubtitle')}
          className="fb-modal"
          busy={sending}
          footer={
            sent ? undefined : (
              <>
                <Button type="button" variant="outline" onClick={close} disabled={sending}>
                  {t('feedback.cancel')}
                </Button>
                <Button type="button" disabled={!canSend} onClick={send}>
                  <Icon name="send" size={16} />
                  {sending ? t('feedback.sending') : t('feedback.title')}
                </Button>
              </>
            )
          }
        >
          {sent ? (
            <div className="fb-success" role="status">
              <span className="fb-ok" aria-hidden="true">
                <Icon name="check" size={26} />
              </span>
              <div className="fb-ok-t">{t('feedback.successTitle')}</div>
              <p className="fb-ok-d">
                {t('feedback.successDetail', {
                  screenshot: shot ? t('feedback.successScreenshot') : '',
                })}
              </p>
              <Button type="button" onClick={close}>
                {t('feedback.done')}
              </Button>
            </div>
          ) : (
            <div className="fb-form">
              <p className="fb-note">
                <Icon name="info" size={16} />
                <span>{t('feedback.formNote')}</span>
              </p>
              <div className="fb-group">
                <span className="fb-sec" id="fb-kind">
                  {t('feedback.whatKind')}
                </span>
                <div className="fb-types" role="group" aria-labelledby="fb-kind">
                  {TYPES.map(([val, labelKey, icon]) => (
                    <button
                      key={val}
                      type="button"
                      className={cn('fb-type', type === val && 'on')}
                      aria-pressed={type === val}
                      onClick={() => setType(val)}
                    >
                      <Icon name={icon} size={16} />
                      {t(labelKey)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="fb-group">
                <span className="fb-sec" id="fb-rate">
                  {t('feedback.overallExperience')}
                </span>
                <div className="fb-starrow" role="group" aria-labelledby="fb-rate">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <button
                      key={i}
                      type="button"
                      className={cn('fb-star', rating >= i && 'on')}
                      aria-pressed={rating >= i}
                      onClick={() => setRating(i)}
                      title={t('feedback.stars', { count: i })}
                      aria-label={t('feedback.stars', { count: i })}
                    >
                      <Icon name="star" size={22} filled={rating >= i} />
                    </button>
                  ))}
                </div>
              </div>
              <label className="fb-f">
                <span className="fb-label">
                  {t('feedback.descriptionLabel')}
                  <span className="req"> *</span>
                </span>
                <textarea
                  className={cn('fb-ta fb-ta-tall', text && !canSend && 'fb-ta-err')}
                  maxLength={1000}
                  rows={5}
                  placeholder={t('feedback.descriptionPlaceholder')}
                  value={text}
                  aria-invalid={Boolean(text) && trimmed < MIN_CHARS}
                  onChange={(e) => setText(e.target.value)}
                />
                <span className="fb-ta-foot">
                  <span className={cn('fb-help', text && trimmed < MIN_CHARS && 'fb-help-err')}>
                    {text && trimmed < MIN_CHARS
                      ? t('feedback.descriptionHintMore', {
                          remaining,
                          suffix: remaining === 1 ? '' : 's',
                        })
                      : t('feedback.descriptionHintDefault')}
                  </span>
                  <span className={cn('fb-count', text.length > 900 && 'fb-count-warn')}>
                    {text.length} / 1000
                  </span>
                </span>
              </label>
              <div className="fb-f">
                <span className="fb-label" id="fb-files">
                  {t('feedback.attachmentsLabel')}
                </span>
                <div className="fb-files">
                  <label className="fb-add">
                    <Icon name="upload_file" size={16} />
                    {t('feedback.addFiles')}
                    <input
                      type="file"
                      className="sr-only"
                      multiple
                      aria-describedby="fb-files"
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                      onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
                    />
                  </label>
                  {files.length > 0 && (
                    <span className="fb-help">
                      {t('feedback.filesAttached', { count: files.length })}
                    </span>
                  )}
                </div>
              </div>
              <div className="fb-attached">
                <Icon name="info" size={16} />
                <div className="fb-att-main">
                  <div className="fb-att-t">{t('feedback.attachedTitle')}</div>
                  <div className="fb-att-v">{t('feedback.attachedValue', { screen })}</div>
                </div>
                <button
                  type="button"
                  className="fb-edit"
                  onClick={() => setShot((v) => !v)}
                  aria-pressed={shot}
                >
                  <Icon name="image" size={16} />
                  {shot ? t('feedback.screenshotOn') : t('feedback.screenshotOff')}
                </button>
              </div>
              {error && (
                <Alert variant="danger" className="fb-error">
                  {error}
                </Alert>
              )}
            </div>
          )}
        </ShellDialog>
      )}
    </>
  );
}
